import { useTranslation } from 'react-i18next';

// Timezone options for the UI
export const timezoneOptions = [
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Chicago', label: 'US Central' },
  { value: 'America/Denver', label: 'US Mountain' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'Europe/London', label: 'UK' },
  { value: 'Europe/Berlin', label: 'Europe Central' },
  { value: 'Asia/Tokyo', label: 'Japan' },
  { value: 'Australia/Sydney', label: 'Australia Eastern' },
  { value: 'UTC', label: 'UTC' }
];

// Localized timezone names - to be used with i18n
export const getTimezoneLabels = (t) => ({
  'UTC': t('therapistdashboardv2.calendar.timezone.utc') || 'UTC',
  'America/New_York': t('therapistdashboardv2.calendar.timezone.usEastern') || 'US Eastern',
  'America/Chicago': t('therapistdashboardv2.calendar.timezone.usCentral') || 'US Central',
  'America/Denver': t('therapistdashboardv2.calendar.timezone.usMountain') || 'US Mountain',
  'America/Los_Angeles': t('therapistdashboardv2.calendar.timezone.usPacific') || 'US Pacific',
  'Europe/London': t('therapistdashboardv2.calendar.timezone.uk') || 'UK',
  'Europe/Berlin': t('therapistdashboardv2.calendar.timezone.europeCentral') || 'Europe Central',
  'Asia/Tokyo': t('therapistdashboardv2.calendar.timezone.japan') || 'Japan',
  'Australia/Sydney': t('therapistdashboardv2.calendar.timezone.australiaEastern') || 'Australia Eastern'
});

// Get timezone options with localized labels
export const getTimezoneOptions = (t) => {
  const labels = getTimezoneLabels(t);
  return timezoneOptions.map(option => ({
    value: option.value,
    label: labels[option.value] || option.label
  }));
};

/**
 * Ensure we have a proper Date object
 * @param {Date|string} dateInput - Date or string
 * @returns {Date} Date object
 */
export const ensureDateObject = (dateInput) => {
  if (!dateInput) return new Date();
  if (typeof dateInput === 'string') {
    return new Date(dateInput);
  }
  return dateInput;
};

/**
 * Get display time for a date in the target timezone
 * This keeps the UTC time the same but displays it as if in the target timezone
 * @param {Date|string} date - The UTC timestamp to convert for display
 * @param {string} timezone - Target timezone for display
 * @returns {Date} Date object adjusted for display in target timezone
 */
export const getDisplayTime = (date, timezone) => {
  if (!date) return null;
  
  // Ensure we have a Date object
  const utcDate = ensureDateObject(date);
  
  try {
    // For development environment where we can't rely on browser timezone APIs
    // Create a display date using the browser's representation of the UTC time
    const displayDate = new Date(utcDate.toISOString());
    
    // This properly handles display for UI components while maintaining the original UTC time
    return displayDate;
  } catch (error) {
    console.error("Error in getDisplayTime:", error);
    return utcDate; // Fallback to the original date
  }
};

/**
 * Format a date for display with proper time formatting
 * @param {Date|string} date - The date to format
 * @param {string} format - Output format 
 * @param {string} timezone - Target timezone
 * @param {string} locale - The locale for formatting (e.g., 'en', 'ja')
 * @returns {string} Formatted date string
 */
export const formatInTimezone = (date, format, timezone, locale = 'en') => {
  if (!date) return '';
  
  // Convert to timezone-aware display date
  const tzDate = getDisplayTime(date, timezone);
  
  // Use Intl.DateTimeFormat for locale-aware formatting
  if (format === 'h:mm a') {
    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(tzDate);
  } else if (format === 'MMM d') {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric'
    }).format(tzDate);
  } else if (format === 'MMMM yyyy') {
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric'
    }).format(tzDate);
  } else if (format === 'EEEE, MMMM d, yyyy') {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(tzDate);
  } else if (format === 'EEE') {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short'
    }).format(tzDate);
  } else if (format === 'd') {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric'
    }).format(tzDate);
  } else if (format === 'yyyy-MM-dd\'T\'HH:mm') {
    // ISO format for datetime-local input
    const year = tzDate.getFullYear();
    const month = String(tzDate.getMonth() + 1).padStart(2, '0');
    const day = String(tzDate.getDate()).padStart(2, '0');
    const hours = String(tzDate.getHours()).padStart(2, '0');
    const minutes = String(tzDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } else if (format === 'HH:mm') {
    // 24-hour format
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(tzDate);
  }
  
  // Default to locale-based formatting if format not recognized
  return new Intl.DateTimeFormat(locale).format(tzDate);
};

/**
 * Get formatted time with AM/PM in the user's locale
 * @param {Date} date - The date to format
 * @param {string} locale - Locale for formatting
 * @returns {string} Formatted time string
 */
export const getLocalizedTime = (date, locale = 'en') => {
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

/**
 * Get localized name of the timezone
 * @param {string} timezone - Timezone identifier
 * @param {function} t - Translation function
 * @returns {string} Localized timezone name
 */
export const getLocalizedTimezoneName = (timezone, t) => {
  const labels = getTimezoneLabels(t);
  return labels[timezone] || timezone;
};

// Debug utilities
export const getTimezoneDebugInfo = (date, timezone) => {
  if (!date) return null;
  
  const originalDate = ensureDateObject(date);
  const displayDate = getDisplayTime(date, timezone);
  
  return {
    timezone,
    originalISOString: originalDate.toISOString(),
    originalTime: `${originalDate.getUTCHours()}:${originalDate.getUTCMinutes()} UTC`,
    displayISOString: displayDate.toISOString(),
    displayTime: `${displayDate.getHours()}:${displayDate.getMinutes()} local`,
    displayTimeString: displayDate.toLocaleTimeString()
  };
};

// Hook to get timezone utilities with localization
export const useTimezoneUtils = () => {
  const { t, i18n } = useTranslation();
  
  return {
    getDisplayTime,
    formatTime: (date, format, timezone) => formatInTimezone(date, format, timezone, i18n.language),
    getTimezoneOptions: () => getTimezoneOptions(t),
    getLocalizedTimezoneName: (timezone) => getLocalizedTimezoneName(timezone, t),
    ensureDateObject,
    currentLocale: i18n.language
  };
};